import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { moduleApi } from "../../lib/api";

/**
 * Aperçu fidèle de l'e-mail d'alerte réellement reçu par un client (sujet + HTML déjà
 * personnalisé pour lui), ouvert depuis l'icône verte "alerte envoyée" (BoardDetail.tsx).
 * Demandé par Emeni le 17/08 — même présentation (iframe sandboxée) que l'aperçu d'e-mail déjà
 * utilisé dans RDV Pro (PrestationPlanningModule.tsx), pour rester cohérent dans toute l'appli.
 */
export function AlertPreviewModal({ reservationId, onClose }: { reservationId: number; onClose: () => void }) {
  const [preview, setPreview] = useState<{ email: string; sentAt: string; subject: string; html: string | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    moduleApi.getExceptionServiceAlertPreview(reservationId)
      .then((d: any) => {
        if (d?.error) { setError(d.error); return; }
        setPreview(d);
      })
      .catch(() => setError("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [reservationId]);

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-surface border border-border rounded-3xl max-h-[85vh] shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border shrink-0">
          <div className="min-w-0">
            <h3 className="font-bold text-content text-sm truncate">{preview?.subject || "Alerte envoyée"}</h3>
            {preview && (
              <p className="text-xs text-content-muted mt-0.5 truncate">
                Envoyé à {preview.email} le {new Date(preview.sentAt).toLocaleString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto bg-surface-soft">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
          ) : error ? (
            <p className="text-xs text-content-muted italic">{error === "Aucune alerte envoyée" ? "Aucune alerte n'a été envoyée pour cette réservation." : error}</p>
          ) : preview?.html ? (
            <div className="bg-white rounded-xl overflow-hidden border border-border">
              <iframe srcDoc={preview.html} className="w-full" style={{ height: "55vh", border: "none" }} sandbox="" title="Aperçu de l'email" />
            </div>
          ) : (
            <p className="text-xs text-content-muted italic">Le contenu de cet e-mail n'a pas été conservé (envoyé avant l'ajout de cet aperçu) — impossible de l'afficher.</p>
          )}
        </div>
        <div className="flex justify-end p-5 border-t border-border shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content text-sm font-bold">Fermer</button>
        </div>
      </div>
    </div>
  );
}
